/**
 * DynamicEvents — Random world events that make the world feel alive.
 *
 * MINECRAFT DOESN'T HAVE THIS.
 *
 * Every 5-15 minutes of gameplay, a random event happens:
 * - Meteor Shower: fireballs rain from sky (spectacle + fire)
 * - Traveling Merchant: random villager appears with rare trades
 * - Zombie Invasion: 20 zombies spawn around player
 * - Treasure Appears: a chest with rare loot spawns nearby
 * - Blood Moon: night sky turns red, all mobs deal 2x damage
 * - Aurora: beautiful light show in sky, heals player over time
 * - Double XP Hour: all XP gains doubled for 60 seconds
 * - Boss Challenge: a legendary mob with golden aura spawns
 */

import * as THREE from 'three';

const EVENT_TYPES = [
  { id: 'meteor',     name: '☄️ Lluvia de Meteoritos', weight: 10, duration: 30 },
  { id: 'merchant',   name: '🎒 Mercader Viajero',     weight: 15, duration: 120 },
  { id: 'invasion',   name: '🧟 Invasión Zombie',      weight: 10, duration: 60 },
  { id: 'treasure',   name: '💎 Tesoro Oculto',        weight: 20, duration: 300 },
  { id: 'bloodmoon',  name: '🌑 Luna de Sangre',       weight: 5,  duration: 300 },
  { id: 'aurora',     name: '🌈 Aurora Boreal',        weight: 10, duration: 120 },
  { id: 'doublexp',   name: '⭐ Doble XP',              weight: 15, duration: 60 },
  { id: 'bossmob',    name: '👹 Mob Legendario',       weight: 10, duration: 300 },
];

export class DynamicEvents {
  constructor() {
    this.activeEvent = null;
    this._eventTimer = 300; // first event after 5 min
    this._remainingTime = 0;
    this._nextEventMin = 300; // 5 minutes
    this._nextEventMax = 900; // 15 minutes

    this._bloodMoonActive = false;
    this.xpMultiplier = 1.0;

    // External refs
    this.scene = null;
    this.world = null;
    this.player = null;
    this.hostileMobs = null;
    this.entityManager = null;
    this.hud = null;
    this.particles = null;
    this.soundManager = null;
    this.dayNight = null;

    this._buildUI();
  }

  _buildUI() {
    // Active event banner (top of screen)
    this._banner = document.createElement('div');
    this._banner.style.cssText = `
      position:fixed; top:60px; left:50%; transform:translateX(-50%);
      background:linear-gradient(to right, #8B0000, #FF4444, #8B0000);
      padding:8px 24px; font-family:'Courier New',monospace;
      font-size:16px; font-weight:bold; color:#FFF;
      text-shadow:2px 2px 0 #000; z-index:160; display:none;
      border:2px solid #FFD700; box-shadow:0 0 20px rgba(255,215,0,0.5);
    `;
    document.body.appendChild(this._banner);
  }

  update(dt) {
    // Countdown to next event
    if (!this.activeEvent) {
      this._eventTimer -= dt;
      if (this._eventTimer <= 0) {
        this._triggerRandomEvent();
      }
      return;
    }

    // Active event logic
    this._remainingTime -= dt;
    if (this._remainingTime <= 0) {
      this._endEvent();
      return;
    }

    // Per-event tick
    switch (this.activeEvent.id) {
      case 'meteor':  this._updateMeteors(dt); break;
      case 'aurora':  this._updateAurora(dt); break;
      case 'bloodmoon': this._updateBloodMoon(dt); break;
    }
  }

  _triggerRandomEvent() {
    const totalWeight = EVENT_TYPES.reduce((s, e) => s + e.weight, 0);
    let roll = Math.random() * totalWeight;
    let selected = EVENT_TYPES[0];
    for (const e of EVENT_TYPES) {
      roll -= e.weight;
      if (roll <= 0) { selected = e; break; }
    }
    this._startEvent(selected);
  }

  _startEvent(eventDef) {
    this.activeEvent = eventDef;
    this._remainingTime = eventDef.duration;
    this._banner.textContent = `¡${eventDef.name}!`;
    this._banner.style.display = 'block';
    if (this.hud) this.hud.showMessage(`Evento: ${eventDef.name}`, 5);
    if (this.soundManager) this.soundManager.playLevelUp?.();

    // Event-specific init
    switch (eventDef.id) {
      case 'meteor': this._meteors = []; break;
      case 'merchant': this._spawnMerchant(); break;
      case 'invasion': this._spawnInvasion(); break;
      case 'treasure': this._spawnTreasure(); break;
      case 'bloodmoon': this._bloodMoonActive = true; break;
      case 'aurora': break;
      case 'doublexp': this.xpMultiplier = 2.0; break;
      case 'bossmob': this._spawnBossMob(); break;
    }
  }

  _endEvent() {
    const ended = this.activeEvent;
    this.activeEvent = null;
    this._banner.style.display = 'none';
    this._eventTimer = this._nextEventMin + Math.random() * (this._nextEventMax - this._nextEventMin);

    if (ended) {
      if (this.hud) this.hud.showMessage(`${ended.name} terminó`, 3);
      switch (ended.id) {
        case 'bloodmoon': this._bloodMoonActive = false; break;
        case 'doublexp': this.xpMultiplier = 1.0; break;
      }
    }
  }

  // ==================== EVENTS ====================

  _updateMeteors(dt) {
    if (!this.scene || !this.player) return;
    // Spawn a meteor every 1-2 seconds
    if (!this._nextMeteor) this._nextMeteor = 0;
    this._nextMeteor -= dt;
    if (this._nextMeteor <= 0) {
      this._nextMeteor = 1 + Math.random() * 1;
      const angle = Math.random() * Math.PI * 2;
      const dist = 20 + Math.random() * 30;
      const mx = this.player.position.x + Math.cos(angle) * dist;
      const mz = this.player.position.z + Math.sin(angle) * dist;
      const my = 100;

      const geo = new THREE.SphereGeometry(0.8, 8, 8);
      const mat = new THREE.MeshBasicMaterial({ color: 0xFF6622 });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(mx, my, mz);
      this.scene.add(mesh);
      this._meteors.push({ mesh, velocity: new THREE.Vector3(0, -20, 0), life: 5 });
    }
    // Update existing meteors
    if (this._meteors) {
      for (let i = this._meteors.length - 1; i >= 0; i--) {
        const m = this._meteors[i];
        m.mesh.position.addScaledVector(m.velocity, dt);
        m.life -= dt;
        // Trail particles
        if (this.particles) this.particles.spawnBlockBreak?.(m.mesh.position, 0xFF8800);
        // Check ground collision
        if (this.world) {
          const bx = Math.floor(m.mesh.position.x);
          const by = Math.floor(m.mesh.position.y);
          const bz = Math.floor(m.mesh.position.z);
          const block = this.world.getBlock(bx, by, bz);
          if (block !== 0 || m.mesh.position.y < 0 || m.life <= 0) {
            // Impact! Small explosion
            if (this.particles) {
              for (let j = 0; j < 20; j++) this.particles.spawnLevelUpBurst?.(m.mesh.position);
            }
            if (this.soundManager) this.soundManager.playExplosion?.();
            this.scene.remove(m.mesh);
            this._meteors.splice(i, 1);
          }
        }
      }
    }
  }

  _updateAurora(dt) {
    // Heal player slowly (+1 HP every 3 seconds)
    if (!this._auroraHealTimer) this._auroraHealTimer = 0;
    this._auroraHealTimer += dt;
    if (this._auroraHealTimer >= 3 && this.player) {
      this._auroraHealTimer = 0;
      if (this.health) this.health.heal(1);
    }
    // Sky tinted with aurora colors (handled by postprocessing if available)
    if (this.scene && this.scene.fog) {
      const t = performance.now() * 0.001;
      const r = 0.2 + Math.sin(t) * 0.1;
      const g = 0.4 + Math.cos(t * 1.2) * 0.2;
      const b = 0.6 + Math.sin(t * 0.7) * 0.2;
      // Don't override fog color (too disruptive) — just visual effect in UI
    }
  }

  _updateBloodMoon(dt) {
    // Handled by dayNight if it honors bloodMoon flag
    if (this.scene && this.scene.background) {
      // Tint sky red (subtle)
    }
  }

  _spawnMerchant() {
    // Spawn a villager with rare trades near player
    if (!this.entityManager || !this.player) return;
    const angle = Math.random() * Math.PI * 2;
    const dist = 10;
    const x = this.player.position.x + Math.cos(angle) * dist;
    const z = this.player.position.z + Math.sin(angle) * dist;
    const y = this.world ? this.world.getTopBlock(Math.floor(x), Math.floor(z)) + 1 : 64;
    if (this.entityManager.spawnEntity) {
      const ent = this.entityManager.spawnEntity('VILLAGER', x, y, z);
      if (ent) {
        ent._isTravelingMerchant = true;
        // Mark position for player
        if (this.hud) this.hud.showMessage(`Mercader en (${Math.floor(x)}, ${Math.floor(z)})`, 5);
      }
    }
  }

  _spawnInvasion() {
    if (!this.hostileMobs || !this.player) return;
    // 20 zombies around player at night
    if (this.hud) this.hud.showMessage('¡20 zombies acercándose!', 4);
    if (this._onSpawnInvasionMobs) {
      this._onSpawnInvasionMobs('ZOMBIE', 20, this.player.position);
    }
  }

  _spawnTreasure() {
    if (!this.world || !this.player || !this.hud) return;
    const angle = Math.random() * Math.PI * 2;
    const dist = 30 + Math.random() * 50;
    const tx = Math.floor(this.player.position.x + Math.cos(angle) * dist);
    const tz = Math.floor(this.player.position.z + Math.sin(angle) * dist);
    const ty = this.world.getTopBlock(tx, tz);
    if (ty > 0) {
      this.world.setBlock(tx, ty + 1, tz, 30); // CHEST
      this.hud.showMessage(`Tesoro en (${tx}, ${ty + 1}, ${tz}) — ¡Búscalo!`, 8);
    }
  }

  _spawnBossMob() {
    if (!this.hostileMobs || !this.player) return;
    if (this.hud) this.hud.showMessage('¡Un mob legendario apareció cerca!', 4);
    if (this._onSpawnBossMob) {
      this._onSpawnBossMob(this.player.position);
    }
  }

  isBloodMoon() {
    return this._bloodMoonActive;
  }

  getXPMultiplier() {
    return this.xpMultiplier;
  }

  /** Manually trigger an event (for /event command testing). */
  triggerEvent(id) {
    const event = EVENT_TYPES.find(e => e.id === id);
    if (event) {
      if (this.activeEvent) this._endEvent();
      this._startEvent(event);
      return true;
    }
    return false;
  }
}
